// Putting a PROJECT'S WORKS on the diary over several days (Session BDM), on the project's page.
//
// ONE JOB, ONE REFERENCE. The first time, this raises the project's single "Project works" job (linked to the project) with the lead engineer
// and crew chosen here; after that it REUSES that job and changes its days, so a project never has two. The days are a start date and a number
// of working days (weekends and bank holidays skipped unless included). It is PREVIEWED first (the days it creates and the capacity and leave
// warnings, which never block) and then confirmed.
//
// IT NEVER BILLS AND NEVER SENDS: a project's works are paid for through the project's own payment milestones, so nothing here raises an invoice
// or messages the customer, and the project's status is not changed. Said on the panel in those words.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { JobDaysPlan, JobDaysView, ScheduleEngineer } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatWeekdayShort } from "../../lib/format";

const SCHEDULABLE = ["accepted", "works_scheduled", "in_progress"];
const field = "min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green";
const btn = "min-h-11 rounded-ele border border-ele-purple-accent px-3 font-heading text-xs tracking-wide text-ele-body transition hover:bg-ele-inset disabled:opacity-50";

// A London calendar day ("YYYY-MM-DD") in words, through the platform's one date formatter: "Thu 15 Oct 2026".
const dayWords = (ymd: string): string => `${formatWeekdayShort(`${ymd}T12:00:00Z`)} ${ymd.slice(0, 4)}`;
const sentence = (err: unknown, fallback: string): string => (err instanceof ApiError && err.body && typeof err.body.message === "string" ? err.body.message : fallback);

export default function ProjectWorksScheduler({ projectId, status }: { projectId: string; status: string }) {
  const [existing, setExisting] = useState<{ jobId: string | null; days: JobDaysView | null } | null>(null);
  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);
  const [startDate, setStartDate] = useState("");
  const [workingDays, setWorkingDays] = useState(5);
  const [weekends, setWeekends] = useState(false);
  const [holidays, setHolidays] = useState(false);
  const [lead, setLead] = useState("");
  const [crew, setCrew] = useState<string[]>([]);
  const [plan, setPlan] = useState<{ plan: JobDaysPlan; raises: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const ready = SCHEDULABLE.includes(status);

  const load = useCallback(async () => {
    try {
      const r = await api.projects.workDays.get(projectId);
      setExisting({ jobId: r.jobId, days: r.days });
    } catch {
      setExisting({ jobId: null, days: null });
    }
  }, [projectId]);
  useEffect(() => {
    if (!ready) return;
    void load();
    try {
      api.scheduling.engineers().then(setEngineers).catch(() => setEngineers([]));
    } catch {
      setEngineers([]);
    }
  }, [load, ready]);

  const body = (preview: boolean) => ({
    startDate,
    workingDays,
    includeWeekends: weekends,
    includeBankHolidays: holidays,
    ...(existing?.jobId ? {} : { engineerId: lead || undefined, crewEngineerIds: crew.length ? crew : undefined }),
    preview,
  });

  async function check() {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const r = await api.projects.workDays.schedule(projectId, body(true));
      setPlan({ plan: r.plan, raises: r.willRaiseJob === true });
    } catch (err) {
      setPlan(null);
      setError(sentence(err, "That could not be checked. Please try again."));
    } finally {
      setBusy(false);
    }
  }
  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.projects.workDays.schedule(projectId, body(false));
      setPlan(null);
      setDone("The works are on the diary. It is one job with one reference; the engineers see it on their phones and calendars.");
      await load();
    } catch (err) {
      setError(sentence(err, "That could not be done. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  if (!ready) {
    return (
      <div className="rounded-ele border border-ele-purple-accent p-4" data-testid="project-works-scheduler">
        <h3 className="mb-1 font-heading text-lg text-ele-strong">Works on the diary</h3>
        <p className="text-sm text-ele-body">Works can be put on the diary once the project is accepted.</p>
      </div>
    );
  }

  const days = existing?.days?.days ?? [];
  return (
    <div className="rounded-ele border border-ele-purple-accent p-4" data-testid="project-works-scheduler">
      <h3 className="mb-1 font-heading text-lg text-ele-strong">Works on the diary</h3>
      <p className="text-sm text-ele-body">
        The works are ONE job over their working days: one reference, shown on every engineer&apos;s phone and calendar for each day they are on. The job raises no
        invoice and sends the customer nothing: the project is billed through its payment milestones.
      </p>

      {existing?.jobId && days.length > 0 && (
        <div className="mt-3 text-sm">
          <p className="text-ele-strong">
            Scheduled over {days.length} working day{days.length === 1 ? "" : "s"}: {dayWords(days[0]!.day)} to {dayWords(days[days.length - 1]!.day)}.{" "}
            <Link to={`/office/scheduling?job=${encodeURIComponent(existing.jobId)}&date=${days[0]!.day}`} className="text-ele-green underline-offset-2 hover:underline">
              Open it on the Scheduling page
            </Link>{" "}
            to extend, shorten or change who is working a day.
          </p>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="grid gap-1 text-xs text-ele-body">
          First day
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={field} />
        </label>
        <label className="grid gap-1 text-xs text-ele-body">
          Working days
          <input type="number" min={1} max={90} value={workingDays} onChange={(e) => setWorkingDays(Math.max(1, Number(e.target.value) || 1))} className={`${field} w-24`} />
        </label>
        {!existing?.jobId && (
          <label className="grid gap-1 text-xs text-ele-body">
            Lead engineer
            <select value={lead} onChange={(e) => setLead(e.target.value)} className={field}>
              <option value="">Not named yet</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {!existing?.jobId && lead && engineers.length > 1 && (
        <fieldset className="mt-2 text-xs text-ele-body">
          <legend>Also attending (up to three)</legend>
          <div className="flex flex-wrap gap-3">
            {engineers
              .filter((e) => e.id !== lead)
              .map((e) => (
                <label key={e.id} className="flex min-h-11 items-center gap-2 text-ele-strong">
                  <input
                    type="checkbox"
                    checked={crew.includes(e.id)}
                    disabled={!crew.includes(e.id) && crew.length >= 3}
                    onChange={(ev) => setCrew((c) => (ev.target.checked ? [...c, e.id] : c.filter((x) => x !== e.id)))}
                  />
                  {e.name}
                </label>
              ))}
          </div>
        </fieldset>
      )}
      <div className="mt-2 flex flex-wrap gap-4 text-xs text-ele-body">
        <label className="flex min-h-11 items-center gap-2">
          <input type="checkbox" checked={weekends} onChange={(e) => setWeekends(e.target.checked)} /> Include Saturdays and Sundays
        </label>
        <label className="flex min-h-11 items-center gap-2">
          <input type="checkbox" checked={holidays} onChange={(e) => setHolidays(e.target.checked)} /> Include bank holidays
        </label>
      </div>
      <button type="button" className={`${btn} mt-2`} disabled={busy || !startDate} onClick={() => void check()}>
        {existing?.jobId ? "Check the new days" : "Check these days"}
      </button>

      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {done && <p role="status" className="mt-2 text-sm text-ele-strong">{done}</p>}

      {plan && (
        <div className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-inset p-3 text-sm" data-testid="project-works-preview">
          <p className="text-ele-strong">
            {plan.raises ? "This raises the project's works job over" : "This changes the works job to"} {plan.plan.next.length} working day{plan.plan.next.length === 1 ? "" : "s"}:{" "}
            {plan.plan.next.map(dayWords).join("; ")}.
          </p>
          {plan.plan.removed.length > 0 && <p className="text-ele-body">Removes: {plan.plan.removed.map(dayWords).join("; ")}.</p>}
          {plan.plan.warnings.capacityWarning && <p className="mt-1 text-ele-body">{plan.plan.warnings.capacityWarning}</p>}
          {plan.plan.warnings.leaveWarning && <p className="mt-1 text-ele-body">{plan.plan.warnings.leaveWarning}</p>}
          <div className="mt-2 flex gap-2">
            <ConfirmButton onConfirm={() => void confirm()} label={plan.raises ? "Put the works on the diary" : "Change the days"} busy={busy} busyLabel="Working..." className={btn} />
            <button type="button" className={btn} onClick={() => setPlan(null)}>
              Leave it as it is
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
